/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAssignedDigitalTrays } from '../../AssignedDigitalTrays.context'
import { useUndocumentedAssetsContext } from 'lib/context/UndocumentedAssetsContext'
import { useBetterIdLookupQuery, useUploadAssetMedia } from 'lib/apollo/hooks'
import { bidSearchResponseToResults } from 'lib/utils/betterid'
import { enqueueSnackbar } from 'notistack'

import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { BetterIdLookupQuery, BetterIdResult } from 'common/types'
import { UsagePlateWithDescription } from '../TrayProducts.types'
import { UsageCustomImplant } from '../../AssignedDigitalTrays.types'
import {
  PlateKey,
  plateDescriptionMap,
} from 'views/SPDLayout/SPD/TrayMap/TrayMap.types'
import { ZimmerCurvedRodsPlateKey } from 'components/organisms/ZimmerRodTrayMap/ZimmerRodTrayMap.types'
import { ZimmerTransverseConnectorsPlateKey } from 'components/organisms/ZimmerTransverseConnectorsTrayMap/ZimmerTransverseConnectors.types'
import { ZimmerStandardClosureTopsPlateKey } from 'components/organisms/ZimmerStandardClosureTopsTrayMap/ZimmerStandardClosureTops.types'
import { QrCode2 } from '@mui/icons-material'
import { Typography } from '@mui/material'
import TrayProduct from './TrayProduct'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'

export const useTrayProducts = () => {
  const navigate = useNavigate()

  const { addUndocumentedScan, deleteScans, undocumentedScans } =
    useUndocumentedAssetsContext()
  const {
    usageScrews,
    selectedTray,
    usagePlates,
    usageOtherItems: usageOtherItemsFromContext,
    selectedSubTray,
    setUsagePlates,
    setUsageScrews,
    setUsageOtherItems,
    usageCustomImplants,
    setUsageCustomImplants,
  } = useAssignedDigitalTrays()
  const [uploadFiles] = useUploadAssetMedia()

  // Transform usageOtherItems by expanding items with count > 1 into multiple items
  // Ensure count is set to 1 for each expanded item to avoid processing issues
  const usageOtherItems = usageOtherItemsFromContext.flatMap(
    ({ count, ...item }) => {
      // Create an array with 'count' copies of the item
      return Array(count)
        .fill(null)
        .map(() => ({ ...item, count: 1 }))
    }
  )

  // Tracks the device ID we're currently looking up:
  const [codeValue, setCodeValue] = useState<string>('')

  // Controls to know which phase the workflow is in:
  const [isSearching, setIsSearching] = useState<boolean>(false)
  /**
   * `isFinished` signals that screws are finished.
   * We'll use `arePlatesDone` to signal that plates are done.
   * Once both screws & plates are done and we still have other items, we process them.
   */
  const [isFinished, setIsFinished] = useState<boolean>(false)
  const [arePlatesDone, setArePlatesDone] = useState<boolean>(false)
  const [areCustomImplantsDone, setAreCustomImplantsDone] =
    useState<boolean>(false)

  // Indices to track progress for screws, plates, and other items:
  const [currentIndex, setCurrentIndex] = useState<number>(-1)
  const [surgeryPlateIndex, setSurgeryPlateIndex] = useState<number>(-1)
  const [otherItemIndex, setOtherItemIndex] = useState<number>(-1)
  const [customImplantIndex, setCustomImplantIndex] = useState<number>(-1)

  // Accumulates the newly created scan IDs so we can attach the tray image at the end if needed:
  const [dtmUndocumentedScanIds, setDtmUndocumentedScanIds] = useState<
    string[]
  >([])
  const [allScanIds, setAllScanIds] = useState<string[]>([])
  const [isProcessingComplete, setIsProcessingComplete] =
    useState<boolean>(false)

  // Categories for screws:
  const [wastedScrews, setWastedScrews] = useState<BetterIDTrayScrew[]>([])
  const [implantedScrews, setImplantedScrews] = useState<BetterIDTrayScrew[]>(
    []
  )

  const subTrayDetails = extractSubTrayJSONData(selectedSubTray)
  const trayImg = subTrayDetails?.trayImg

  /**
   * For scanning, we rely on a "BetterID" query:
   * When `isSearching` becomes true (i.e., we just set `codeValue`),
   * we do an "onCompleted" callback to handle the results.
   */
  useBetterIdLookupQuery(codeValue, undefined, {
    skip: !isSearching,
    onCompleted: (data) => {
      handleLookupCompletion(data)
    },
  })

  /**
   * Observes the undocumented scans and grabs only the ones flagged with `isDTMScrew`:
   * We'll remove them from the store once we start a new workflow run.
   */
  useEffect(() => {
    if (undocumentedScans.length > 0) {
      const dtmScans = undocumentedScans
        .filter((scan) => scan.isDTMScrew)
        .map((scan) => scan._id)
      setDtmUndocumentedScanIds(dtmScans)
    }
  }, [undocumentedScans])

  /**
   * Splits usageScrews into wasted vs. implanted for display (and for when we create the data below).
   */
  useEffect(() => {
    setWastedScrews(usageScrews?.filter((trayProduct) => trayProduct.wasted))
    setImplantedScrews(
      usageScrews?.filter((trayProduct) => !trayProduct.wasted)
    )
  }, [usageScrews])

  /**
   * When the final phase is complete, always navigate regardless of scan IDs or tray image
   */
  useEffect(() => {
    if (isProcessingComplete) {
      // If we have scan IDs and a tray image, we could upload the tray image
      if (allScanIds.length > 0 && trayImg) {
        // uploadTrayImage(allScanIds)
      }

      // Always clean up and navigate when processing is complete,
      // regardless of whether there are scan IDs or tray images
      setIsProcessingComplete(false) // Reset for next batch if needed
      setAllScanIds([]) // Clear scan IDs
      setUsagePlates([])
      setUsageScrews([])
      setUsageOtherItems([])
      setUsageCustomImplants([])
      navigate(`../asset/batch/undocumented`)
    }
  }, [isProcessingComplete])

  /**
   * These effect blocks watch our index-based steps and decide what to look up next:
   *  1) If we haven't finished screws, continue with them.
   *  2) If screws are done but plates are not, handle plates.
   *  3) If plates are done, handle custom implants.
   *  4) If custom implants are done, handle other items (if any).
   */
  useEffect(() => {
    // If we are still handling screws:
    if (shouldSearchForScrews()) {
      setCodeValue(usageScrews[currentIndex]?.deviceId as string)
      setIsSearching(true)
    }
    // If screws are finished, handle plates:
    else if (isFinished && shouldSearchForPlates() && !arePlatesDone) {
      setCodeValue(surgeryUsagePlates[surgeryPlateIndex]?.deviceId as string)
      setIsSearching(true)
    }
    // If plates are done, handle custom implants:
    else if (
      arePlatesDone &&
      shouldSearchForCustomImplants() &&
      !areCustomImplantsDone
    ) {
      setCodeValue(usageCustomImplants[customImplantIndex]?.deviceId as string)
      setIsSearching(true)
    }
    // If custom implants are done, handle other items if they exist:
    else if (areCustomImplantsDone && shouldSearchForOtherItems()) {
      setCodeValue(usageOtherItems[otherItemIndex]?.deviceId as string)
      setIsSearching(true)
    }
  }, [
    currentIndex,
    isFinished,
    surgeryPlateIndex,
    arePlatesDone,
    customImplantIndex,
    areCustomImplantsDone,
    otherItemIndex,
  ])

  /**
   * Workflow entry method:
   *  - Clears out any old scans if needed
   *  - Kicks off screws scanning if they exist
   *  - Otherwise sets us on to plates (if no screws)
   *  - If no plates either, moves to custom implants
   *  - If no custom implants either, moves to other items if any
   */
  const handleSubmit = () => {
    if (undocumentedScans.length > 0) {
      deleteScans(dtmUndocumentedScanIds)
    }

    // Start with screws if any:
    if (usageScrews.length > 0) {
      setCurrentIndex(0)
      setIsSearching(true)
    } else {
      // If no screws, mark them "finished" so we can jump immediately to plates:
      setIsFinished(true)

      // Check if there are plates to process
      if (surgeryUsagePlates.length > 0) {
        setSurgeryPlateIndex(0)
      } else {
        // If no plates either, mark plates as done and move to custom implants
        setArePlatesDone(true)

        if (usageCustomImplants.length > 0) {
          setCustomImplantIndex(0)
          setIsSearching(true)
        } else {
          // If no custom implants either, mark them as done and move to other items
          setAreCustomImplantsDone(true)

          if (usageOtherItems.length > 0) {
            setOtherItemIndex(0)
            setIsSearching(true)
          } else {
            // No screws, no plates, no custom implants, no other items - we're done
            setIsProcessingComplete(true)
          }
        }
      }
    }
  }

  /**
   * Called whenever the BetterID query finishes for the current `codeValue`.
   * We add the returned results (sometimes there are multiple results for one query).
   * Once that is done, we move our indexes (screws, plates, or items).
   */
  const handleLookupCompletion = async (data: BetterIdLookupQuery) => {
    setIsSearching(false)
    const selectedResults = bidSearchResponseToResults(data.betterIdLookup)
    let newScanIds: string[] = []

    for (const selectedResult of selectedResults) {
      try {
        const addedScan = await addUndocumentedScan({
          variables: {
            data: [createScanData(selectedResult)],
          },
        })

        if (!addedScan.data?.addUndocumentedScan[0]?._id) {
          console.error('Failed to add undocumented scan - no ID returned', {
            selectedResult,
          })
          enqueueSnackbar('Failed to process scan data', { variant: 'error' })
          continue
        }

        newScanIds.push(addedScan.data.addUndocumentedScan[0]._id)
      } catch (error) {
        console.error('Failed to process scan:', {
          error,
          deviceId: selectedResult.deviceId,
        })
        enqueueSnackbar(
          'Failed to process scan. Please try again or contact support if the issue persists.',
          { variant: 'error' }
        )
      }
    }

    // Keep track of any newly created scan IDs.
    setAllScanIds((prev) => [...prev, ...newScanIds])

    // Move on to the next usage item in the relevant category:
    updateIndices()
    checkNavigationConditions()
  }

  /**
   * Creates the data object to be passed to `addUndocumentedScan` from a single search result.
   */
  const createScanData = (selectedResult: BetterIdResult) => {
    /**
     * For simplicity, we check:
     *  - If `!isFinished`, we're handling screws.
     *  - Else if `!arePlatesDone`, we're handling plates.
     *  - Else if `!areCustomImplantsDone`, we're handling custom implants.
     *  - Else we're handling other items.
     */
    const isHandlingScrews = !isFinished
    const isHandlingPlates = isFinished && !arePlatesDone
    const isHandlingCustomImplants = arePlatesDone && !areCustomImplantsDone

    // Return the data object for the item type we're scanning:
    return {
      bidAssetId: selectedResult.id,
      bidCompanyId: selectedResult.companyId,
      catalogNumber: selectedResult.catalogNumber,
      deviceDescription: selectedResult.deviceDescription,
      deviceId: selectedResult.deviceId,
      deviceCount: selectedResult.deviceCount,
      count: 1,
      pkgQuantity: selectedResult.pkgQuantity,
      expirationDate: selectedResult.expirationDate,
      lotBatch: selectedResult.lotBatch,
      companyName: selectedResult.company?.name,
      manufacturingDate: selectedResult.manufacturingDate,
      versionModelNumber: selectedResult.versionModelNumber,
      serialNumber: selectedResult.serialNumber,
      sizeText: selectedResult.sizeText,
      sizeString: selectedResult.sizeString,
      udi: selectedResult.udi,
      issuingAgency: selectedResult.issuingAgency,
      secondaryDeviceIdType: selectedResult.secondaryDeviceIdType,
      assetType: 'non-biological',
      // Always mark these as DTM for the moment if we want them grouped:
      isDTMScrew: true,

      dtmScrewData: {
        // For screws:
        column: isHandlingScrews ? usageScrews[currentIndex].x : null,
        row: isHandlingScrews ? usageScrews[currentIndex].row : null,
        label: isHandlingScrews
          ? usageScrews[currentIndex].label
          : isHandlingPlates
          ? surgeryUsagePlates[surgeryPlateIndex].plateName
          : isHandlingCustomImplants
          ? undefined
          : usageOtherItems[otherItemIndex].deviceDescription,
        wasted: isHandlingScrews
          ? usageScrews[currentIndex].wasted
          : isHandlingPlates
          ? surgeryUsagePlates[surgeryPlateIndex].wasted
          : isHandlingCustomImplants
          ? (usageCustomImplants[customImplantIndex] as UsageCustomImplant)
              .wasted
          : usageOtherItems[otherItemIndex].wasted,
        trayId: selectedSubTray?.id,
        size: isHandlingScrews ? usageScrews[currentIndex].column : null,
        isProductInGUDID: selectedResult?.isProductInGUDID ?? true,
      },
    }
  }

  /**
   * Increments the relevant index (screws, plates, custom implants, or other items).
   */
  const updateIndices = () => {
    // If we haven't marked screws as finished, we are scanning them:
    if (!isFinished) {
      setCurrentIndex((prevIndex) => prevIndex + 1)
      return
    }

    // If screws are finished but plates are not done, we handle plates:
    if (isFinished && !arePlatesDone) {
      setSurgeryPlateIndex((prevIndex) => prevIndex + 1)
      return
    }

    // If plates are done but custom implants are not, we handle custom implants:
    if (arePlatesDone && !areCustomImplantsDone) {
      setCustomImplantIndex((prevIndex) => prevIndex + 1)
      return
    }

    // If custom implants are done, we handle other items:
    setOtherItemIndex((prevIndex) => prevIndex + 1)
  }

  /**
   * Checks if we need to switch from screws => plates => custom implants => other items => "done"
   */
  const checkNavigationConditions = () => {
    // If we just scanned the final screw:
    if (currentIndex === usageScrews.length - 1 && !isFinished) {
      // Move on to plates:
      setSurgeryPlateIndex(0)
      setIsFinished(true)
      // If no plates at all, skip straight to custom implants or finish:
      if (surgeryUsagePlates.length === 0) {
        setArePlatesDone(true)
        if (usageCustomImplants.length === 0) {
          setAreCustomImplantsDone(true)
          if (usageOtherItems.length === 0) {
            setIsProcessingComplete(true)
          } else {
            setOtherItemIndex(0)
          }
        } else {
          setCustomImplantIndex(0)
        }
      }
      return
    }

    // If we just scanned the final plate:
    if (
      isFinished &&
      !arePlatesDone &&
      surgeryPlateIndex === surgeryUsagePlates.length - 1
    ) {
      setArePlatesDone(true)
      // Move on to custom implants if any:
      if (usageCustomImplants.length === 0) {
        setAreCustomImplantsDone(true)
        if (usageOtherItems.length === 0) {
          setIsSearching(false)
          setIsProcessingComplete(true)
        } else {
          setOtherItemIndex(0)
        }
      } else {
        setCustomImplantIndex(0)
      }
      return
    }

    // If we just scanned the final custom implant:
    if (
      arePlatesDone &&
      !areCustomImplantsDone &&
      customImplantIndex === usageCustomImplants.length - 1
    ) {
      setAreCustomImplantsDone(true)
      // Move on to other items if any:
      if (usageOtherItems.length === 0) {
        setIsSearching(false)
        setIsProcessingComplete(true)
      } else {
        setOtherItemIndex(0)
      }
      return
    }

    // If custom implants are done and we just scanned the final other item:
    if (
      areCustomImplantsDone &&
      otherItemIndex === usageOtherItems.length - 1
    ) {
      setIsSearching(false)
      setIsProcessingComplete(true)
    }
  }

  /**
   * Boolean checks to see if we're still scanning screws or plates or custom implants or other items:
   */
  const shouldSearchForScrews = () =>
    usageScrews.length - 1 >= currentIndex && currentIndex >= 0

  const shouldSearchForPlates = () =>
    surgeryUsagePlates.length - 1 >= surgeryPlateIndex && surgeryPlateIndex >= 0

  const shouldSearchForCustomImplants = () =>
    usageCustomImplants.length - 1 >= customImplantIndex &&
    customImplantIndex >= 0

  const shouldSearchForOtherItems = () =>
    usageOtherItems.length - 1 >= otherItemIndex && otherItemIndex >= 0

  /**
   * Finds a textual label for the given plate key (just a small helper for clarity).
   */
  const findPlateDescriptionByKey = (
    plateName:
      | PlateKey
      | ZimmerCurvedRodsPlateKey
      | ZimmerTransverseConnectorsPlateKey
      | ZimmerStandardClosureTopsPlateKey
  ) => {
    return plateDescriptionMap[plateName as PlateKey]
  }

  const surgeryUsagePlates = (usagePlates || []).map((usagePlate) => ({
    ...usagePlate,
    description: findPlateDescriptionByKey(usagePlate.plateName as PlateKey),
  }))

  /**
   * Renders screws for either wasted or implanted categories.
   */
  const renderScrewData = (
    screws: BetterIDTrayScrew[],
    emptyMessage: string
  ) => (
    <>
      {screws.map((product: BetterIDTrayScrew, _index: number) => (
        <TrayProduct key={`${product.deviceId}${_index}`} {...product} />
      ))}
      {screws.length === 0 && (
        <Typography variant="body2" className="empty-data_text">
          {emptyMessage}
        </Typography>
      )}
    </>
  )

  /**
   * Renders usage plates that we have scanned or are about to scan:
   */
  const renderUsagePlates = (
    usagePlates: UsagePlateWithDescription[],
    emptyMessage: string
  ) => {
    return (
      <>
        {usagePlates.map((usagePlate) => (
          <div key={usagePlate.plateName} className="usage-plate_container">
            <QrCode2 className="qr-code" />
            <div className="content">
              <Typography variant="subtitle2">
                Manufacturer:{' '}
                {usagePlate?.plateName?.includes('connector')
                  ? 'Zimmer Biomet'
                  : 'Stryker'}
              </Typography>
              <Typography variant="subtitle2">
                Device ID: {usagePlate.deviceId}
              </Typography>
              <Typography variant="subtitle2">
                {usagePlate.wasted ? 'Wasted' : 'Implanted'}
              </Typography>
            </div>
          </div>
        ))}
        {usagePlates.length === 0 && (
          <Typography variant="body2" className="empty-data_text">
            {emptyMessage}
          </Typography>
        )}
      </>
    )
  }

  /**
   * Renders "other items" similarly to how screws and plates are displayed.
   */
  const renderOtherItems = (
    items: BetterIDTrayScrew[],
    emptyMessage: string
  ) => (
    <>
      {items.map((product: BetterIDTrayScrew, _index: number) => (
        <TrayProduct key={`${product.deviceId}${_index}`} {...product} />
      ))}
      {items.length === 0 && (
        <Typography variant="body2" className="empty-data_text">
          {emptyMessage}
        </Typography>
      )}
    </>
  )

  /**
   * An optional function to attach a tray image to the newly created scans, if we want that:
   */
  const uploadTrayImage = async (scanIds: string[]) => {
    const trayImgUrl = subTrayDetails?.trayImg

    if (!trayImgUrl) {
      console.warn('No tray image available')
      return
    }

    try {
      const response = await fetch(trayImgUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch tray image: ${response.statusText}`)
      }

      const blob = await response.blob()
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = () =>
          reject(new Error('Failed to convert image to base64'))
        reader.readAsDataURL(blob)
      })

      const uploadResponse = await uploadFiles({
        variables: {
          scanIds,
          files: [
            {
              filename: `tray-image-${Date.now()}.jpg`,
              mimetype: 'image/jpeg',
              encoding: 'base64',
              content: base64Content.split(',')[1],
            },
          ],
        },
      })

      if (!uploadResponse.data?.uploadAssetMedia?.files) {
        throw new Error('No files returned from upload')
      }
    } catch (error) {
      console.error('Failed to attach tray image:', { error, scanIds })
      enqueueSnackbar(
        "Scans were processed but failed to attach tray image. This won't affect functionality.",
        { variant: 'warning' }
      )
    }
  }

  return {
    implantedScrews,
    wastedScrews,
    handleSubmit,
    isSearching,
    surgeryUsagePlates,
    renderScrewData,
    renderUsagePlates,
    renderOtherItems,
  }
}
